import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Bot, Loader2, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ToolCallIndicator } from './ToolCallIndicator'
import type { ChatMessage } from '@/stores'

interface MessageProps {
  message: ChatMessage
}

export function Message({ message }: MessageProps) {
  const isUser = message.role === 'user'

  return (
    <div
      className={cn(
        'group flex gap-3 px-4 py-4',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-primary' : 'bg-gradient-to-br from-primary/20 to-[hsl(280,100%,70%)]/20'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-primary-foreground" />
        ) : (
          <Bot className="h-4 w-4 text-primary" />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          'max-w-[80%] px-4 py-2.5',
          isUser
            ? 'bg-primary/90 text-primary-foreground rounded-2xl rounded-br-md'
            : 'bg-muted/30 rounded-2xl rounded-bl-md text-foreground'
        )}
      >
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        ) : (
          <div className="text-sm prose prose-sm prose-chat max-w-none break-words [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>li]:my-0.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
            {message.isStreaming && !message.content && !message.activeToolCall && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span>Thinking...</span>
              </div>
            )}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code: ({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { className?: string }) => {
                  const isBlock = className?.startsWith('language-')
                  if (isBlock) {
                    return (
                      <pre className="bg-muted/60 rounded-lg p-3 overflow-x-auto my-2">
                        <code className={cn('text-sm font-mono', className)} {...props}>{children}</code>
                      </pre>
                    )
                  }
                  return <code className="bg-muted/60 px-1.5 py-0.5 rounded text-primary font-mono text-[0.85em]" {...props}>{children}</code>
                },
                pre: ({ children }) => <>{children}</>,
                a: ({ children, ...props }) => <a {...props} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                table: ({ children, ...props }) => <div className="overflow-x-auto my-2"><table {...props} className="min-w-full text-sm border-collapse">{children}</table></div>,
                th: ({ children, ...props }) => <th {...props} className="border border-border/50 px-3 py-1.5 text-left font-medium bg-muted/30">{children}</th>,
                td: ({ children, ...props }) => <td {...props} className="border border-border/50 px-3 py-1.5">{children}</td>,
              }}
            >
              {message.content}
            </ReactMarkdown>
            {message.isStreaming && message.content && (
              <span className="inline-block w-2 h-4 ml-0.5 bg-primary rounded-sm animate-pulse" />
            )}
          </div>
        )}

        {/* Tool call indicator */}
        {message.activeToolCall && (
          <ToolCallIndicator toolName={message.activeToolCall} />
        )}

        <p
          className={cn(
            'text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity',
            isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  )
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}
